<script setup lang="ts">
/**
 * Context menu: opens at a point (`x`/`y`, the right-click) or beside an
 * element (`anchor`, a „…“ button), with an optional head (`title` /
 * `subtitle`, or the `header` slot) and the entries in the default slot —
 * `NxContextMenuItem`, `NxContextSubmenu`, `NxContextMenuSeparator`,
 * `NxContextMenuLabel`.
 *
 * `v-model:open` drives it; `close` reports why it closed. Keyboard, outside
 * clicks, scroll, resize and focus return are handled (see NxMenuPanel). It
 * lives in the popover top layer, flips and shifts to stay on screen.
 * `useNxContextMenu()` wires a `@contextmenu` handler in one line.
 */
import { computed } from 'vue';
import NxMenuPanel, { type NxMenuCloseReason } from './NxMenuPanel.vue';

const props = withDefaults(
  defineProps<{
    /** Viewport point to open at (clientX / clientY of the event). */
    x?: number;
    y?: number;
    /** Element to open beside instead of a point. */
    anchor?: HTMLElement | null;
    title?: string;
    subtitle?: string;
    ariaLabel?: string;
    width?: 'sm' | 'md' | 'lg' | 'auto';
    /**
     * The area the menu keeps inside instead of the viewport — a cell's list
     * stays in the grid and never covers the sheet tabs below it.
     */
    boundary?: HTMLElement | null;
  }>(),
  {
    x: 0,
    y: 0,
    anchor: null,
    title: undefined,
    subtitle: undefined,
    ariaLabel: 'Aktionen',
    width: 'md',
    boundary: null,
  }
);

const open = defineModel<boolean>('open', { required: true });
const emit = defineEmits<{ close: [reason: NxMenuCloseReason] }>();

const reference = computed(() => {
  if (props.anchor) return props.anchor;
  const { x, y } = props;
  return { getBoundingClientRect: () => new DOMRect(x, y, 0, 0) };
});

function onClose(reason: NxMenuCloseReason) {
  open.value = false;
  emit('close', reason);
}
</script>

<template>
  <NxMenuPanel
    :open="open"
    :reference="reference"
    :placement="props.anchor ? 'bottom-end' : 'bottom-start'"
    :offset="props.anchor ? 4 : 0"
    :aria-label="props.title ?? props.ariaLabel"
    :width="props.width"
    :boundary="props.boundary"
    :ignore="props.anchor ? [props.anchor] : []"
    :return-focus-to="props.anchor"
    data-testid="nx-context-menu"
    @close="onClose"
  >
    <div v-if="$slots.header || props.title" class="nx-context-menu__head" role="presentation">
      <slot name="header">
        <strong class="nx-context-menu__title">{{ props.title }}</strong>
        <span v-if="props.subtitle" class="nx-context-menu__subtitle">{{ props.subtitle }}</span>
      </slot>
    </div>
    <slot />
  </NxMenuPanel>
</template>

<style scoped>
.nx-context-menu__head {
  display: grid;
  gap: var(--nxt-space-2xs);
  margin-block-end: var(--nxt-space-2xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm) var(--nxt-space-sm);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
  min-width: 0;
}
.nx-context-menu__title {
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-context-menu__subtitle {
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
